import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosInstance';
import { ShopBannerLiveState } from '../types';

export type ShopBannerStyle = 'yellow' | 'green' | 'black';
export const COPY_KEYS = ['eyebrow', 'headline', 'subline', 'small_print'] as const;
export type CopyKey = (typeof COPY_KEYS)[number];
export type Translations = Record<string, Partial<Record<CopyKey, string>>>;

/** Status of a recurring banner, computed in Brussels time. */
export interface RecurringStatus {
  state: 'live' | 'next' | 'paused' | 'off' | 'unpublished';
  /** "Live now" · "Next: Mon 19:00" · "Paused until 25/10 (included)" · "Off" · "Not published". */
  label: string;
  next_start: string | null;
  paused_until: string | null;
}

export interface ShopBanner {
  id: number | null;
  /** Campaign banners only (the recurring endpoints don't send it). */
  exists?: boolean;
  placement: number | null;
  collection_id: string;
  collection_title: string;
  collection_handle: string;
  starts_at: string | null;
  ends_at: string | null;
  /** Brussels dates 'YYYY-MM-DD': live from start_date 00:00 until
   *  end_date 00:00 (end date exclusive — last full day is the day before). */
  start_date: string | null;
  end_date: string | null;
  countries: string[];
  priority: number;
  style: ShopBannerStyle;
  timer_hours: number | null;
  eyebrow: string;
  headline: string;
  subline: string;
  small_print: string;
  translations: Translations;
  shopify_id: string;
  shopify_status: string;
  pushed_at: string | null;
  push_error: string;
  handle: string;
  live_state: ShopBannerLiveState;
  in_sync: boolean | null;
  missing: string[];
  /** Recurring banners (Nightly sale, Weekend sale): no placement, no dates. */
  is_recurring: boolean;
  name: string;
  /** ISO weekdays the window STARTS on, 1 = Monday … 7 = Sunday. */
  repeat_days: number[];
  /** Local wall-clock minutes after midnight (New York time for US/CA/MX, Brussels otherwise). */
  repeat_start_minute: number;
  repeat_minutes: number;
  /** Inclusive Brussels dates 'YYYY-MM-DD'. */
  paused_from: string | null;
  paused_until: string | null;
  enabled: boolean;
  recurring_status: RecurringStatus | null;
  /** "every Monday to Friday from 19:00 until midnight". */
  schedule: string | null;
}

export type ShopBannerInput = Partial<
  Pick<
    ShopBanner,
    | 'collection_id' | 'collection_title' | 'collection_handle'
    | 'start_date' | 'end_date' | 'countries' | 'priority' | 'style'
    | 'timer_hours' | 'eyebrow' | 'headline' | 'subline' | 'small_print' | 'translations'
    | 'repeat_days' | 'repeat_start_minute' | 'repeat_minutes' | 'paused_from' | 'paused_until' | 'enabled'
  >
>;

export interface ShopBannerMeta {
  configured: boolean;
  locales: string[];
  publish_active: boolean;
  handle_prefix: string;
  /** Claude connection for "Suggest copy" / "Translate" (ANTHROPIC_API_KEY set). */
  ai_configured: boolean;
  error: string | null;
}

export interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
  products_count: number | null;
  /** null = unknown (couldn't check the Online Store channel). */
  published: boolean | null;
}

export interface ShopBannerRow {
  placement_id: number;
  placement_title: string;
  placement_status: string;
  campaign: string | null;
  campaign_name: string | null;
  publish_date: string | null;
  end_date: string | null;
  banner: ShopBanner | null;
  live_state: ShopBannerLiveState;
  in_sync: boolean | null;
  ended: boolean;
}

export interface ShopBannerList {
  campaigns: ShopBannerRow[];
  recurring: ShopBanner[];
}

/** Which banner an editor works on: a placement's, or a recurring one by id. */
export interface BannerRef {
  kind: 'placement' | 'recurring';
  id: number;
}

const basePath = (ref: BannerRef) =>
  ref.kind === 'placement' ? `api/assets/${ref.id}/shop-banner/` : `api/shop-banners/recurring/${ref.id}/`;

const key = (ref: BannerRef) => ['shop-banner', ref.kind, ref.id] as const;

export const useShopBanner = (ref: BannerRef) =>
  useQuery({
    queryKey: key(ref),
    queryFn: async (): Promise<ShopBanner> => (await axios.get(basePath(ref))).data,
    enabled: !!ref.id,
  });

export const useShopBannerMeta = () =>
  useQuery({
    queryKey: ['shop-banners', 'meta'],
    queryFn: async (): Promise<ShopBannerMeta> => (await axios.get('api/shop-banners/meta/')).data,
    staleTime: 60 * 60 * 1000,
  });

export const useShopBannerList = (includeEnded: boolean) =>
  useQuery({
    queryKey: ['shop-banners', 'list', includeEnded],
    queryFn: async (): Promise<ShopBannerList> =>
      (await axios.get(`api/shop-banners/${includeEnded ? '?include_ended=true' : ''}`)).data,
  });

export const useCollectionSearch = (q: string, enabled: boolean) =>
  useQuery({
    queryKey: ['shopify-collections', q],
    queryFn: async (): Promise<ShopifyCollection[]> =>
      (await axios.get(`api/shopify/collections/?q=${encodeURIComponent(q)}`)).data,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

const useBannerMutation = <V,>(ref: BannerRef, fn: (v: V) => Promise<ShopBanner>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.setQueryData(key(ref), data);
      qc.invalidateQueries({ queryKey: ['shop-banners', 'list'] });
      if (ref.kind === 'placement') {
        qc.invalidateQueries({ queryKey: ['assets'] });
        qc.invalidateQueries({ queryKey: ['asset', ref.id] });
      }
    },
  });
};

export const useSaveShopBanner = (ref: BannerRef) =>
  useBannerMutation(ref, async (input: ShopBannerInput) =>
    (await axios.put(basePath(ref), input)).data);

export const usePublishShopBanner = (ref: BannerRef) =>
  useBannerMutation(ref, async (_: void) =>
    (await axios.post(`${basePath(ref)}publish/`)).data);

export const useUnpublishShopBanner = (ref: BannerRef) =>
  useBannerMutation(ref, async (_: void) =>
    (await axios.post(`${basePath(ref)}unpublish/`)).data);

export type CopyFields = Record<CopyKey, string>;
export interface CopyOption extends CopyFields {
  angle: string;
}

/** Three copy options from Claude, based on the campaign in mosy (or the
 *  recurring schedule) plus the editor's current (unsaved) values. ~15 s. */
export const suggestShopBannerCopy = async (
  ref: BannerRef,
  body: Partial<CopyFields> & {
    brief: string;
    collection_title: string;
    start_date?: string | null;
    end_date?: string | null;
    repeat_days?: number[];
    repeat_start_minute?: number;
    repeat_minutes?: number;
    timer_hours?: number | null;
  },
): Promise<CopyOption[]> =>
  (await axios.post(`${basePath(ref)}suggest/`, body, { timeout: 120_000 })).data.options;

/** Claude translations of the English copy into `locales`. */
export const translateShopBannerCopy = async (
  ref: BannerRef,
  copy: CopyFields,
  locales: string[],
): Promise<Record<string, CopyFields>> =>
  (await axios.post(`${basePath(ref)}translate/`, { ...copy, locales }, { timeout: 120_000 })).data.translations;

export const fetchShopTranslations = async (ref: BannerRef): Promise<Translations> =>
  (await axios.get(`${basePath(ref)}shop-translations/`)).data.translations;

/** Server error text from an axios error, if any. */
export const errorDetail = (err: unknown): string | null => {
  const data = (err as { response?: { data?: unknown } } | null)?.response?.data;
  if (!data) return null;
  if (typeof data === 'string') return data.slice(0, 200);
  if (typeof data === 'object') {
    const d = data as Record<string, unknown>;
    if (typeof d.detail === 'string') return d.detail;
    const first = Object.entries(d)[0];
    if (first) {
      const [k, v] = first;
      return `${k}: ${Array.isArray(v) ? v.join(' ') : String(v)}`;
    }
  }
  return null;
};
